import {
    Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ContentMedia } from '@app/_components/content';
import { MediaUploadItem, MediaUploadService } from '@app/_services/media-upload.service';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

@Component({
    selector: 'app-media-tab',
    templateUrl: './media-tab.component.html',
    styleUrls: ['./media-tab.component.scss'],
    standalone: false
})
export class MediaTabComponent implements OnInit, OnDestroy {

    @Input() mediaList: ContentMedia[] = [];
    @Output() mediaListChange = new EventEmitter<ContentMedia[]>();

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    queue: MediaUploadItem[] = [];
    dragOver = false;

    private sub: Subscription;

    constructor(
        private mediaUploadService: MediaUploadService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.sub = this.mediaUploadService.getQueue().subscribe(q => {
            this.queue = q;
            const done = q.filter(i => i.status === 'done');
            done.forEach(item => {
                const alreadyInList = this.mediaList.some(m => m.fileName === item.fileName);
                if (!alreadyInList && item.remoteUrl) {
                    const media: ContentMedia = {
                        contentMediaId: null,
                        contentId: null,
                        mediaType: item.mediaType === 'video' ? 2 : 1,
                        title: item.file.name,
                        caption: null,
                        altText: null,
                        fileName: item.fileName,
                        amazonBucketName: 'img.kbmhawaii.com',
                        url: item.remoteUrl,
                        thumbnailUrl: null,
                        width: null,
                        height: null,
                        fileSizeBytes: item.sizeBytes,
                        mimeType: item.mimeType,
                        sortOrder: this.mediaList.length,
                        featured: false,
                        enabled: true
                    };
                    this.mediaList = [...this.mediaList, media];
                    this.mediaListChange.emit(this.mediaList);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        const files = Array.from(event.clipboardData?.files || []);
        if (files.length > 0) {
            event.preventDefault();
            this.processFiles(files);
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = true;
    }

    onDragLeave(): void {
        this.dragOver = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragOver = false;
        const files = Array.from(event.dataTransfer?.files || []);
        this.processFiles(files);
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files || []);
        this.processFiles(files);
        input.value = '';
    }

    openFilePicker(): void {
        this.fileInput.nativeElement.click();
    }

    onDrop2(event: CdkDragDrop<ContentMedia[]>): void {
        const list = [...this.mediaList];
        moveItemInArray(list, event.previousIndex, event.currentIndex);
        list.forEach((m, i) => m.sortOrder = i);
        this.mediaList = list;
        this.mediaListChange.emit(this.mediaList);
    }

    removeMedia(index: number): void {
        const list = [...this.mediaList];
        list.splice(index, 1);
        this.mediaList = list;
        this.mediaListChange.emit(this.mediaList);
    }

    removeQueueItem(id: string): void {
        this.mediaUploadService.remove(id);
    }

    retryUpload(id: string): void {
        this.mediaUploadService.retry(id).subscribe();
    }

    trackById(index: number, item: MediaUploadItem): string {
        return item.id;
    }

    trackByUrl(index: number, item: ContentMedia): string {
        return item.url || String(index);
    }

    pendingCount(): number {
        return this.queue.filter(i => i.status === 'pending' || i.status === 'uploading').length;
    }

    private processFiles(files: File[]): void {
        files.forEach(file => {
            if (!IMAGE_TYPES.includes(file.type) && !VIDEO_TYPES.includes(file.type)) {
                this.messageService.add({ severity: 'warn', summary: 'Unsupported file', detail: `${file.name} is not a supported image or video type.` });
                return;
            }
            const isVideo = VIDEO_TYPES.includes(file.type);
            const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
            if (file.size > maxBytes) {
                const limitMb = maxBytes / (1024 * 1024);
                this.messageService.add({ severity: 'warn', summary: 'File too large', detail: `${file.name} exceeds the ${limitMb}MB limit.` });
                return;
            }
            this.mediaUploadService.upload(file).subscribe();
        });
    }
}
